/**
 * ThemeEngine - نظام إدارة الألوان والقوالب المتقدم
 */

import React, { useMemo, useEffect } from 'react';
import type { Element } from '@/types/page-editor';

interface ThemeConfig {
  primary: string;
  secondary: string;
  accent: string;
  muted: string;
  background: string;
  foreground: string;
  card: string;
  border: string;
  destructive: string;
  success: string;
  warning: string;
  info: string;
}

interface ThemeEngineProps {
  element: Element;
  children: React.ReactNode;
  className?: string;
}

/**
 * Hook لتطبيق ألوان القالب على العناصر
 */
export const useElementTheme = (element: Element) => {
  return useMemo(() => {
    const result = {
      themeClasses: [] as string[],
      cssVariables: {} as Record<string, string>,
      resolvedColors: {} as Record<string, string>
    };

    // Get computed theme colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    
    const themeConfig: ThemeConfig = {
      primary: rootStyles.getPropertyValue('--primary') || '222.2 84% 4.9%',
      secondary: rootStyles.getPropertyValue('--secondary') || '210 40% 96%',
      accent: rootStyles.getPropertyValue('--accent') || '210 40% 96%',
      muted: rootStyles.getPropertyValue('--muted') || '210 40% 96%',
      background: rootStyles.getPropertyValue('--background') || '0 0% 100%',
      foreground: rootStyles.getPropertyValue('--foreground') || '222.2 84% 4.9%',
      card: rootStyles.getPropertyValue('--card') || '0 0% 100%',
      border: rootStyles.getPropertyValue('--border') || '214.3 31.8% 91.4%',
      destructive: rootStyles.getPropertyValue('--destructive') || '0 84.2% 60.2%',
      success: rootStyles.getPropertyValue('--success') || '142.1 76.2% 36.3%',
      warning: rootStyles.getPropertyValue('--warning') || '47.9 95.8% 53.1%',
      info: rootStyles.getPropertyValue('--info') || '198.6 88.7% 48.4%'
    };

    // Apply theme colors to element if using theme variables
    if (element.advancedStyles?.backgroundColor?.startsWith('--theme-')) {
      const colorKey = element.advancedStyles.backgroundColor.replace('--theme-', '') as keyof ThemeConfig;
      if (themeConfig[colorKey]) {
        result.resolvedColors.backgroundColor = `hsl(${themeConfig[colorKey]})`;
        result.cssVariables['--element-bg'] = `hsl(${themeConfig[colorKey]})`;
      }
    }

    if (element.advancedStyles?.textColor?.startsWith('--theme-')) {
      const colorKey = element.advancedStyles.textColor.replace('--theme-', '') as keyof ThemeConfig;
      if (themeConfig[colorKey]) {
        result.resolvedColors.color = `hsl(${themeConfig[colorKey]})`;
        result.cssVariables['--element-color'] = `hsl(${themeConfig[colorKey]})`;
      }
    }

    // Add theme-based classes for styling
    switch (element.type) {
      case 'button':
        result.themeClasses.push('theme-button');
        break;
      case 'heading':
        result.themeClasses.push('theme-heading');
        break;
      case 'text':
        result.themeClasses.push('theme-text');
        break;
      default:
        result.themeClasses.push('theme-element');
    }

    return result;
  }, [element.advancedStyles, element.type]);
};

/**
 * ThemeEngine Component - تطبيق القالب على العناصر
 */
export const ThemeEngine: React.FC<ThemeEngineProps> = ({ 
  element, 
  children, 
  className = '' 
}) => {
  const { themeClasses, cssVariables, resolvedColors } = useElementTheme(element);

  // Apply CSS variables to the document root for global access
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    return () => {
      // Cleanup on unmount
      Object.keys(cssVariables).forEach(key => {
        root.style.removeProperty(key);
      });
    };
  }, [cssVariables]);

  const combinedClasses = [className, ...themeClasses].filter(Boolean).join(' ');

  return (
    <div 
      className={combinedClasses}
      style={resolvedColors}
      data-theme-applied="true"
    >
      {children}
    </div>
  );
};

/**
 * ThemeProvider Component - مزود القالب
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

/**
 * useTheme Hook - هوك استخدام القالب
 */
export const useTheme = () => {
  const rootStyles = getComputedStyle(document.documentElement);
  
  return {
    theme: 'light',
    setTheme: () => {},
    toggleTheme: () => {},
    colors: {
      primary: rootStyles.getPropertyValue('--primary') || '222.2 84% 4.9%',
      secondary: rootStyles.getPropertyValue('--secondary') || '210 40% 96%',
      accent: rootStyles.getPropertyValue('--accent') || '210 40% 96%',
      muted: rootStyles.getPropertyValue('--muted') || '210 40% 96%',
      background: rootStyles.getPropertyValue('--background') || '0 0% 100%',
      foreground: rootStyles.getPropertyValue('--foreground') || '222.2 84% 4.9%',
      card: rootStyles.getPropertyValue('--card') || '0 0% 100%'
    }
  };
};

export default ThemeEngine;